/**
 * useInboxSelection Hook
 *
 * Manages checkbox selection state for bulk inbox actions.
 * Tracks selected message IDs and provides toggle/select-all/clear operations.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import type { UnifiedInboxItem } from '../../../types/app-message.types';

export interface UseInboxSelectionResult {
  /** Set of currently selected message IDs */
  selectedIds: Set<string>;
  /** Toggle selection of a single item */
  toggleSelect: (id: string) => void;
  /** Select all visible items */
  selectAll: (items: UnifiedInboxItem[]) => void;
  /** Clear all selections */
  clearSelection: () => void;
  /** Check if a specific item is selected */
  isSelected: (id: string) => boolean;
  /** Number of currently selected items */
  selectedCount: number;
  /** Check if all visible items are selected */
  isAllSelected: (items: UnifiedInboxItem[]) => boolean;
}

/**
 * Hook for managing inbox message selection state.
 *
 * @param deps - Array of dependencies that should trigger selection clear (e.g., view, filters)
 */
export function useInboxSelection(deps: unknown[] = []): UseInboxSelectionResult {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Clear selection when dependencies change (view, filters, etc.)
  useEffect(() => {
    setSelectedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback((items: UnifiedInboxItem[]) => {
    setSelectedIds(new Set(items.map((item) => item.id)));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback(
    (id: string) => selectedIds.has(id),
    [selectedIds]
  );

  const selectedCount = useMemo(() => selectedIds.size, [selectedIds]);

  const isAllSelected = useCallback(
    (items: UnifiedInboxItem[]) => items.length > 0 && items.every((item) => selectedIds.has(item.id)),
    [selectedIds]
  );

  return {
    selectedIds,
    toggleSelect,
    selectAll,
    clearSelection,
    isSelected,
    selectedCount,
    isAllSelected,
  };
}
